import { useEffect, useRef } from 'react';
import { useT } from '../i18n/i18n';
import type { Role } from './schoolApi';

export type TourTab = 'overview' | 'pupils' | 'classes' | 'parents' | 'staff' | 'settings' | 'audit';

type Step = { tab: TourTab; title: string; body: string };

const ADMIN_STEPS: Step[] = [
  { tab: 'overview', title: 'Welcome to your school\'s area', body: 'This short tour shows what each tab is for. It takes about a minute. You can leave it at any time.' },
  { tab: 'overview', title: 'Overview', body: 'How much each class and each pupil practised, and how often they were right first time. Pupils who have not practised in the last 2 weeks are marked. You can download this as a spreadsheet.' },
  { tab: 'pupils', title: 'Pupils', body: 'Add pupils, change their names and choose their classes. Removing a pupil also removes everything recorded about them.' },
  { tab: 'classes', title: 'Classes', body: 'Make classes and choose who teaches each one. A teacher sees only the pupils in the classes they teach.' },
  { tab: 'parents', title: 'Parents', body: 'Invite parents with their own email address and choose what they can do: see progress, practise at home, change programs and plans, or use the Talk Board. Full access gives all of these. A parent sees only their own child.' },
  { tab: 'staff', title: 'Staff', body: 'Staff sign in with their school account. Choose who is a school admin and who is a teacher, and remove access when someone leaves.' },
  { tab: 'settings', title: 'Settings', body: 'The school\'s name and logo. EasyLearning AI and outside experts are off until you turn them on. You can also download all the school\'s data, or delete all pupil data.' },
  { tab: 'audit', title: 'Audit log', body: 'Every change made here, and every download, with who did it and when. Nobody can change or delete these entries.' },
  { tab: 'overview', title: 'Working with a pupil', body: 'To teach, choose a pupil next to "Working with" at the top of the page. Home, Library and Plan then show that pupil\'s programs and progress.' },
];

const TEACHER_STEPS: Step[] = [
  { tab: 'overview', title: 'Welcome to your school\'s area', body: 'This short tour shows what each tab is for. You can leave it at any time.' },
  { tab: 'overview', title: 'Overview', body: 'How much each pupil in your classes practised, and how often they were right first time. Pupils who have not practised in the last 2 weeks are marked.' },
  { tab: 'pupils', title: 'Pupils', body: 'The pupils in the classes you teach. A school admin adds pupils and changes classes.' },
  { tab: 'classes', title: 'Your classes', body: 'The classes you teach. If one is missing, ask a school admin to add you to it.' },
  { tab: 'parents', title: 'Parents', body: 'Invite the parents of pupils in your classes and choose what they can do. A parent sees only their own child.' },
  { tab: 'overview', title: 'Working with a pupil', body: 'To teach, choose a pupil next to "Working with" at the top of the page. Home, Library and Plan then show that pupil\'s programs and progress.' },
];

export const TOUR_SEEN_KEY = 'el.school.tour.seen';

export function tourSteps(role: Role): Step[] {
  return role === 'ADMIN' ? ADMIN_STEPS : TEACHER_STEPS;
}

export default function SchoolTour({ role, step, onStep, onClose }: {
  role: Role;
  step: number;
  onStep: (next: number) => void;
  onClose: () => void;
}) {
  const t = useT();
  const steps = tourSteps(role);
  const current = steps[step];
  const heading = useRef<HTMLHeadingElement | null>(null);
  const last = step === steps.length - 1;

  useEffect(() => { heading.current?.focus(); }, [step]);

  return (
    <section className="card school-tour" aria-labelledby="school-tour-h" aria-live="polite">
      <p className="muted school-tour-count">{t('Step {n} of {total}', { n: step + 1, total: steps.length })}</p>
      <h3 id="school-tour-h" ref={heading} tabIndex={-1}>{t(current.title)}</h3>
      <p>{t(current.body)}</p>
      <div className="toolbar">
        {step > 0 && <button type="button" className="btn ghost" onClick={() => onStep(step - 1)}>{t('Back')}</button>}
        <button type="button" className="btn" onClick={() => (last ? onClose() : onStep(step + 1))}>{last ? t('Finish') : t('Next')}</button>
        {!last && <button type="button" className="btn ghost" onClick={onClose}>{t('Leave the tour')}</button>}
      </div>
    </section>
  );
}
